import {
  useAllItems,
  type ConversationItem,
} from '@/services/ConversationService';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { Column, Font, PrimaryActionColor, Row } from './DesignSystem';
import { Spacer } from './ColorPreview';
import { useVersions } from './VersionsPreview';
import {
  ComponentPreview,
  useComponentsPreview,
  type ComponentID,
} from './ComponentsPreview';

type ScreenPreviewReducerState = {};

export type ScreenID = string;
export function NewScreenID(): ScreenID {
  return crypto.randomUUID();
}

export type ScreenConnection = {
  name: string;
  from: ScreenID;
  to: ScreenID;
};

export type ResolvedScreenConnection = {
  name: string;
  from: Screen;
  to: Screen;
};

export function resolve(
  connection: ScreenConnection,
  state: ScreenPreviewComputedState,
): ResolvedScreenConnection | null {
  const from = state.screensByID[connection.from];
  if (from === undefined) {
    return null;
  }

  const to = state.screensByID[connection.to];
  if (to === undefined) {
    return null;
  }

  return { name: connection.name, from, to };
}

export type ExperimentalScreenState = {
  description?: string;
  connections?: ScreenConnection[];
};

export type Screen = {
  id: string;
  name: string;
  rootComponentID: ComponentID | undefined;
} & ExperimentalScreenState;

type ScreenPreviewComputedState = {
  screensByID: { [id: ScreenID]: Screen };
};

type ScreenPreviewReducerAction = Record<string, never>;
type ScreenPreviewReducerDispatch = (
  action: ScreenPreviewReducerAction,
) => void;

type ScreenPreviewContextProps = {
  state: ScreenPreviewReducerState;
  dispatch: ScreenPreviewReducerDispatch;
};
type ScreenPreviewExternalContextProps = {
  state: ScreenPreviewReducerState & ScreenPreviewComputedState;
  dispatch: ScreenPreviewReducerDispatch;
};
const ScreenPreviewContext = createContext<ScreenPreviewContextProps | null>(
  null,
);
export function useScreenPreview(): ScreenPreviewExternalContextProps {
  const context = useContext(ScreenPreviewContext);
  if (context === null) {
    throw new Error(
      'useScreenPreview must be used within a ScreenPreviewBoundary',
    );
  }
  const { state, dispatch } = context;
  const {
    state: { activeVersionID },
  } = useVersions();

  const allItems = useAllItems({ activeVersionID });
  function getScreensFromConversation(items: ConversationItem[]): {
    [id: ScreenID]: Screen;
  } {
    return items.reduce<{ [id: ScreenID]: Screen }>((acc, e) => {
      switch (e.type) {
        case 'addScreen': {
          acc[e.id] = { ...e };
          return acc;
        }
        case 'removeScreen': {
          delete acc[e.id];
          return acc;
        }
        case 'describeScreen': {
          const existingScreen = acc[e.id];
          if (existingScreen === undefined) {
            return acc;
          }
          acc[e.id] = { ...existingScreen, description: e.description };
          return acc;
        }
        case 'connect': {
          const existingScreen = acc[e.id];
          if (existingScreen === undefined) {
            return acc;
          }

          const existingConnections = existingScreen.connections ?? [];
          const connections = existingConnections.concat({
            name: e.name,
            from: e.id,
            to: e.to,
          });
          acc[e.id] = { ...existingScreen, connections };
          return acc;
        }
        case 'addChildComponent': {
          if (e.parent.type !== 'screen') {
            // no-op
            return acc;
          }

          const existingScreen = acc[e.parent.id];
          if (existingScreen === undefined) {
            return acc;
          }

          return {
            ...acc,
            [e.parent.id]: {
              ...existingScreen,
              rootComponentID: e.childID,
            },
          };
        }
        case 'defineColor':
        case 'removeColor':
        case 'updateColorPalette':
        case 'message':
        case 'defineTextStyle':
        case 'removeTextStyle':
        case 'updateTextStyles':
        case 'writeProperties':
        case 'defineComponentStyle':
        case 'removeComponentStyle':
        case 'declareFunctionComponent':
        case 'removeFunctionComponent':
        case 'createComponent':

        case 'createVersion':
          // no-op
          return acc;
      }
    }, {});
  }

  const screensByID = useMemo(() => {
    return getScreensFromConversation(allItems);
  }, [allItems]);
  return { state: { ...state, screensByID }, dispatch };
}

export function ScreenPreviewBoundary({ children }: React.PropsWithChildren) {
  function reducer(
    state: ScreenPreviewReducerState,
    action: ScreenPreviewReducerAction,
  ): ScreenPreviewReducerState {
    return state;
  }
  const [state, dispatch] = useReducer(reducer, {});
  return (
    <ScreenPreviewContext.Provider value={{ state, dispatch }}>
      {children}
    </ScreenPreviewContext.Provider>
  );
}

export function ScreenPreview({ screen }: { screen: Screen }) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { state } = useScreenPreview();
  const { state: componentState } = useComponentsPreview();

  const connections =
    screen.connections
      ?.map((c) => resolve(c, state))
      .filter((c) => c !== null) ?? [];

  const rootComponent = useMemo(() => {
    const rootComponentID = screen.rootComponentID;
    if (rootComponentID === undefined) {
      return undefined;
    }
    return componentState.componentsByID[rootComponentID];
  }, [screen.rootComponentID, componentState.componentsByID]);

  useEffect(() => {
    console.log('Rendering root component: ', rootComponent);
  }, [rootComponent]);

  return (
    <Column
      onClick={() => {
        setExpanded(!expanded);
      }}
    >
      <Row style={{ alignItems: 'center', width: '100%' }}>
        <Font kind="font-subtitle">{screen.name}</Font>
        <Spacer />
        <div>
          {connections.map((connection) => {
            return (
              <Column
                key={connection.name + connection.from.id + connection.to.id}
                style={{
                  backgroundColor: PrimaryActionColor + '66',
                  padding: 4,
                  borderRadius: 4,
                }}
              >
                <Font kind="font-body">
                  {connection.name}
                  {' -> '}
                  {connection.to.name}
                </Font>
              </Column>
            );
          })}
        </div>
      </Row>

      {expanded && screen.description && <div>{screen.description}</div>}

      {expanded && rootComponent && (
        <ComponentPreview component={rootComponent} parameters={[]} />
      )}
    </Column>
  );
}

export const EnableScreenPalette = true;
export function MakeInitialScreenConversationItems(): ConversationItem[] {
  return [];
}
